import React, { useMemo, useState } from 'react';
import { bridgeSettingsApi } from '../../apis';
import { SalvButton, SalvLabel, SalvTable, Size } from '@salvapp/ui-components';
import { createColumnHelper } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';
import { CAN_MODIFY_BRIDGE_SETTINGS } from '../../auth/permissions';
import Has from '../../auth/Has';
import { BridgeRecipientConfigurationModal } from './BridgeRecipientConfigurationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';

export interface AttributesRecipientMapping {
  fieldType: string;
  selectedValue: string;
  fieldValues: string[];
  validationStatus?: {
    isValid: boolean;
    errorMessage?: string;
  };
}

export const BridgeRecipientConfigurations = () => {
  const { currentOrganisation } = useMe();
  const columnHelper = createColumnHelper<AttributesRecipientMapping>();

  const { data: configurationsData, isLoading } = useQuery({
    queryKey: ['bridge_recipient_mappings', currentOrganisation?.id],
    queryFn: async () => await bridgeSettingsApi.getBridgeRecipientMappingConfs(),
    initialData: [],
  });

  const { data: attributeMappings } = useQueryAttributeMappings();

  const recipientMappings = useMemo(() => {
    const groupedMap = new Map<string, AttributesRecipientMapping>();

    configurationsData.forEach((config) => {
      config.fieldMappings.forEach((mapping) => {
        const selectedValue = mapping.selectedValue;
        const fieldType = config.fieldType;
        const groupKey = `${selectedValue}-${fieldType}`;

        if (!groupedMap.has(groupKey)) {
          groupedMap.set(groupKey, {
            selectedValue,
            fieldType,
            fieldValues: [],
            validationStatus: {
              isValid: true,
              errorMessage: undefined,
            },
          });
        }

        const group = groupedMap.get(groupKey)!;
        group.fieldValues.push(mapping.fieldValue);

        // Update validation status - if any mapping is invalid, the group is invalid
        if (mapping.validationStatus && !mapping.validationStatus.isValid) {
          group.validationStatus = {
            isValid: false,
            errorMessage: mapping.validationStatus.errorMessage,
          };
        }
      });
    });

    return Array.from(groupedMap.values()).sort((a, b) => {
      // Sort failed validations to the top
      if (!a.validationStatus?.isValid && b.validationStatus?.isValid) {
        return -1;
      }
      if (a.validationStatus?.isValid && !b.validationStatus?.isValid) {
        return 1;
      }
      // Then sort alphabetically by selectedValue
      return a.selectedValue.localeCompare(b.selectedValue);
    });
  }, [configurationsData]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('selectedValue', {
        header: 'Selected Recipient',
        cell: ({ getValue, row }) => (
          <div className='d-flex align-items-center'>
            {row.original.validationStatus && (
              <FontAwesomeIcon
                icon={row.original.validationStatus.isValid ? faCircleCheck : faCircleXmark}
                className={`m-r-8 ${row.original.validationStatus.isValid ? 'text-success' : 'text-danger'}`}
              />
            )}
            <div>
              <div>{getValue()}</div>
              {row.original.validationStatus && !row.original.validationStatus.isValid && (
                <div className='text-danger text-xs'>{row.original.validationStatus.errorMessage}</div>
              )}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('fieldType', {
        header: 'Field Type',
        cell: ({ getValue }) => <SalvLabel variant='secondary'>{getValue()}</SalvLabel>,
      }),
      columnHelper.accessor('fieldValues', {
        header: 'Attribute values',
        cell: ({ getValue }) => (
          <div className='d-flex'>
            {getValue().map((fieldValue, index) => (
              <div key={index} className='m-l-4'>
                <SalvLabel>{fieldValue}</SalvLabel>
              </div>
            ))}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 0,
        cell: ({ row }) => (
          <Has allPermissions={[CAN_MODIFY_BRIDGE_SETTINGS]}>
            <SalvButton
              label='Edit'
              onClick={() => {
                setSelectedMapping(row.original);
                setShowModal(true);
              }}
              size={Size.SMALL}
              variant='muted'
            />
          </Has>
        ),
      }),
    ],
    [configurationsData, recipientMappings, attributeMappings]
  );

  if (configurationsData.length === 0 && isLoading) {
    return <div>Loading configurations...</div>;
  }

  const [showModal, setShowModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<AttributesRecipientMapping | undefined>(undefined);

  return (
    <>
      <SalvTable
        columns={columns}
        data={recipientMappings}
        isLoading={isLoading}
        emptyMessage='No bridge recipient mapping configurations found'
        title='Bridge recipient mappings'
        actions={
          <>
            <Has allPermissions={[CAN_MODIFY_BRIDGE_SETTINGS]}>
              <SalvButton
                label='Add configuration'
                onClick={() => setShowModal(true)}
                size={Size.SMALL}
                variant='muted'
              />
            </Has>
          </>
        }
        className='m-t-16'
        search
        expandable
        defaultExpanded={false}
      />

      <BridgeRecipientConfigurationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedMapping(undefined);
        }}
        fieldTypes={configurationsData.map((conf) => conf.fieldType)}
        mapping={selectedMapping}
        recipientMappings={recipientMappings}
        attributeMappings={attributeMappings}
      />
    </>
  );
};

export default BridgeRecipientConfigurations;
